import * as React from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled, { css } from 'styled-components';
import { faEye } from '@fortawesome/pro-solid-svg-icons/faEye';
import { faFire } from '@fortawesome/pro-solid-svg-icons/faFire';
import { faHeart } from '@fortawesome/pro-solid-svg-icons/faHeart';
import { faPauseCircle } from '@fortawesome/pro-regular-svg-icons/faPauseCircle';
import { faVideo } from '@fortawesome/pro-solid-svg-icons/faVideo';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getCatalog } from '@/redux/modules/catalog';
import { getItem } from '@/redux/modules/item';
import { getLiveCatalogData } from '@/redux/modules/console';
import { getSavedItemCount } from '@/redux/modules/savedItemCount';
import isEqual from 'react-fast-compare';
import Text from '@liveauctioneers/caterwaul-components/lib/Text/Text';
import { useSelector } from 'react-redux';

type Props = {
    catalogId?: number;
    className?: string;
    isOnCatalogCard?: boolean;
    isPaused?: boolean;
    itemId?: number;
    trending?: boolean;
};

const ConsoleSocial = (props: Props) => {
    const { catalogId, className = '', isOnCatalogCard = false, isPaused = false, itemId, trending = false } = props;

    let savedCount = 0;
    let catalogIdFromItem;
    const item = useSelector((state) => getItem(state, itemId), isEqual);
    const saved = useSelector((state) => getSavedItemCount(state, itemId), isEqual);

    if (!catalogId && itemId) {
        catalogIdFromItem = item.catalogId;
        savedCount = saved.savedCount;
    }

    const { absenteeBidders, hasVideo } = useSelector(
        (state) => getCatalog(state, catalogId || catalogIdFromItem),
        isEqual
    );
    const { bidders } = useSelector((state) => getLiveCatalogData(state, catalogId || catalogIdFromItem), isEqual);

    return (
        <ContainerDiv className={className} useColumnStyles={!isOnCatalogCard}>
            <div>
                {(isOnCatalogCard || isPaused) && (
                    <LiveContainerDiv useRedContainerStyles={isOnCatalogCard} useTextOnConsoleLiveStyles={isPaused}>
                        <LiveContainerText colorWhite={isOnCatalogCard} secondary>
                            {hasVideo && <StyledFontAwesomeIcon $useWhite={isOnCatalogCard} icon={faVideo} />}
                            {isPaused && <StyledFontAwesomeIcon $useWhite={isOnCatalogCard} icon={faPauseCircle} />}
                            {isPaused ? <FormattedMessage id="paused" /> : <FormattedMessage id="live" />}
                        </LiveContainerText>
                    </LiveContainerDiv>
                )}
                <EyeStylesText
                    colorWhite={isOnCatalogCard}
                    secondary
                    useBlackContainerStyles={isOnCatalogCard}
                    useTextOnConsoleEyeStyles={isPaused}
                >
                    {trending ? (
                        <StyledFontAwesomeIcon $useWhite={isOnCatalogCard} icon={faFire} />
                    ) : (
                        <StyledFontAwesomeIcon $useWhite={isOnCatalogCard} icon={faEye} />
                    )}
                    <Text colorWhite={isOnCatalogCard} lineHeight={22} secondary>
                        <FormattedNumber
                            maximumFractionDigits={0}
                            minimumFractionDigits={0}
                            value={bidders + absenteeBidders || 1}
                        />
                    </Text>
                </EyeStylesText>
            </div>
            {Boolean(savedCount) && !isOnCatalogCard && (
                <HeartContainerDiv>
                    <StyledFontAwesomeIcon icon={faHeart} />
                    <Text lineHeight={22} secondary>
                        <FormattedNumber maximumFractionDigits={0} minimumFractionDigits={0} value={savedCount} />
                    </Text>
                </HeartContainerDiv>
            )}
        </ContainerDiv>
    );
};

export default React.memo<Props>(ConsoleSocial);

const ColumnStyles = css`
    flex-direction: column;

    & > div:first-child {
        margin-bottom: 5px;
    }
`;

const ContainerDiv = styled.div<{ useColumnStyles: boolean }>`
    position: absolute;
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    padding: 0;
    color: ${({ theme }) => theme.colors.black};
    margin: 7px 10px;
    top: 5px;
    left: 0;
    z-index: 2;
    ${({ useColumnStyles }) => useColumnStyles && ColumnStyles}

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        margin: 18px 0 0 21px;
    }
`;

const ContainerStyles = css`
    border-radius: 2px;
    background-color: ${({ theme }) => theme.colors.shadowOverlay50};
    padding: 0 6px;
    display: inline-block;
    color: ${({ theme }) => theme.colors.grey100};
    text-align: left;
`;

const RedContainerStyles = css`
    background-color: ${({ theme }) => theme.colors.shadowOverlayRed300};
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    color: ${({ theme }) => theme.colors.white};
    text-shadow: none;
    padding: 1px 0 1px 8px;

    span {
        letter-spacing: 1.3px;
        text-transform: uppercase;
    }

    svg {
        color: ${({ theme }) => theme.colors.white};
    }
`;

const TextOnConsoleLiveStyles = css`
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    padding-right: 0;
`;

const LiveContainerDiv = styled.div<{ useRedContainerStyles: boolean; useTextOnConsoleLiveStyles: boolean }>`
    ${ContainerStyles}
    ${({ useRedContainerStyles }) => useRedContainerStyles && RedContainerStyles}
    ${({ useTextOnConsoleLiveStyles }) => useTextOnConsoleLiveStyles && TextOnConsoleLiveStyles}
`;

const LiveContainerText = styled(Text)`
    font-size: 14px;
`;

const StyledFontAwesomeIcon = styled(FontAwesomeIcon)<{ $useWhite?: boolean }>`
    margin-right: 6px;
    color: ${({ $useWhite, theme }) => ($useWhite ? theme.colors.white : theme.colors.grey100)};
    width: 13px;
    height: 13px;
    font-size: 14px;
`;

const BlackContainerStyles = css`
    background-color: ${({ theme }) => theme.colors.shadowOverlayRed300};
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    padding: 1px 8px 1px 6px;
`;

const TextOnConsoleEyeStyles = css`
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
`;

const EyeStylesText = styled(Text)<{ useBlackContainerStyles: boolean; useTextOnConsoleEyeStyles: boolean }>`
    ${ContainerStyles}
    ${({ useBlackContainerStyles }) => useBlackContainerStyles && BlackContainerStyles}
    ${({ useTextOnConsoleEyeStyles }) => useTextOnConsoleEyeStyles && TextOnConsoleEyeStyles}
`;

const HeartContainerDiv = styled.div`
    ${ContainerStyles}
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
`;
